const STORY_STATE_KEY = 'interactive_story';

export const STORY_SCHEMA_VERSION = 2;

export const STORY_TRACK_TYPES = Object.freeze({
  main: Object.freeze({
    value: 'main',
    label: '主线任务',
    shortLabel: '主线',
    code: '主',
    description: '推动核心目标、公共事件和主要剧情进程。',
    color: '#ef4444',
  }),
  male_route: Object.freeze({
    value: 'male_route',
    label: '男主分线',
    shortLabel: '男主线',
    code: '男',
    description: '围绕男主人物关系与个人走向展开的分线。',
    color: '#3b82f6',
  }),
  hidden: Object.freeze({
    value: 'hidden',
    label: '隐藏线',
    shortLabel: '隐藏',
    code: '隐',
    description: '需要特定信息或条件才会被发现的秘密路线。',
    color: '#22c55e',
  }),
  ending_check: Object.freeze({
    value: 'ending_check',
    label: '结局判定',
    shortLabel: '判定',
    code: '判',
    description: '集中检查关系、标记与前置选择，决定最终去向。',
    color: '#a855f7',
  }),
  lock_be: Object.freeze({
    value: 'lock_be',
    label: '短线 BE / 关键锁线',
    shortLabel: 'BE / 锁线',
    code: '锁',
    description: '提前结束的坏结局，或因条件不足而关闭的关键路线。',
    color: '#94a3b8',
  }),
});

export const STORY_TRACK_TYPE_OPTIONS = Object.freeze(Object.values(STORY_TRACK_TYPES));

export const IMPACT_TYPES = Object.freeze({
  main: Object.freeze({
    value: 'main',
    label: '主线抉择',
    shortLabel: '主线',
    description: '改变核心目标、章节路线、重大事件或主要结局。',
    color: '#f97316',
    edgeWidth: 3,
    edgeDasharray: undefined,
  }),
  character: Object.freeze({
    value: 'character',
    label: '人物抉择',
    shortLabel: '人物',
    description: '改变某个人物的关系、立场、个人剧情阶段或最终归宿。',
    color: '#a78bfa',
    edgeWidth: 2.25,
    edgeDasharray: undefined,
  }),
  local: Object.freeze({
    value: 'local',
    label: '局部回应',
    shortLabel: '局部',
    description: '只改变眼前的对白或反应，不必长期改变剧情状态。',
    color: '#64748b',
    edgeWidth: 1.5,
    edgeDasharray: '6 5',
  }),
});

export const IMPACT_TYPE_OPTIONS = Object.freeze(Object.values(IMPACT_TYPES));

export const CONDITION_OPERATORS = Object.freeze([
  { value: 'eq', label: '等于' },
  { value: 'neq', label: '不等于' },
  { value: 'gt', label: '大于' },
  { value: 'gte', label: '大于等于' },
  { value: 'lt', label: '小于' },
  { value: 'lte', label: '小于等于' },
  { value: 'truthy', label: '已成立' },
  { value: 'falsy', label: '未成立' },
  { value: 'in', label: '属于' },
  { value: 'notIn', label: '不属于' },
]);

const CHARACTER_COLORS = [
  '#f97316',
  '#8b5cf6',
  '#06b6d4',
  '#22c55e',
  '#ec4899',
  '#eab308',
];

const VALID_SCOPES = new Set(['relation', 'flag', 'stage']);
const VALID_NODE_TYPES = new Set(['story', 'ending']);
const VALID_TRACK_TYPES = new Set(Object.keys(STORY_TRACK_TYPES));
const VALID_OPERATORS = new Set(CONDITION_OPERATORS.map((item) => item.value));
let fallbackIdSequence = 0;

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  return value == null ? [] : [value];
};

const asObject = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const asText = (value, fallback = '') => (
  value == null ? fallback : String(value)
);

const STORY_ANSWER_HANDLE_PREFIX = 'answer:';

export const createStoryAnswerHandleId = (answerId) => {
  const normalizedId = asText(answerId).trim();
  return normalizedId ? `${STORY_ANSWER_HANDLE_PREFIX}${normalizedId}` : '';
};

export const readStoryAnswerIdFromHandle = (handleId) => {
  const normalizedHandleId = asText(handleId).trim();
  if (!normalizedHandleId.startsWith(STORY_ANSWER_HANDLE_PREFIX)) return '';
  return normalizedHandleId.slice(STORY_ANSWER_HANDLE_PREFIX.length).trim();
};

const normalizeScope = (value) => {
  const scope = asText(value).trim().toLowerCase();
  if (scope === 'relationship' || scope === 'affection') return 'relation';
  if (scope === 'boolean') return 'flag';
  return VALID_SCOPES.has(scope) ? scope : 'flag';
};

const normalizeImpactType = (value) => {
  const impactType = asText(value).trim().toLowerCase();
  if (impactType === 'critical' || impactType === 'key') return 'main';
  if (impactType === 'person' || impactType === 'relationship') return 'character';
  return IMPACT_TYPES[impactType] ? impactType : 'local';
};

const normalizeStoryTrackType = (value, nodeType = 'story', endingType = null) => {
  const rawTrackType = asText(value).trim().toLowerCase();
  const aliases = {
    mainline: 'main',
    red: 'main',
    romance: 'male_route',
    male: 'male_route',
    character: 'male_route',
    blue: 'male_route',
    secret: 'hidden',
    green: 'hidden',
    ending: 'ending_check',
    ending_gate: 'ending_check',
    gate: 'ending_check',
    judge: 'ending_check',
    purple: 'ending_check',
    be: 'lock_be',
    lock: 'lock_be',
    locked: 'lock_be',
    gray: 'lock_be',
    grey: 'lock_be',
  };
  const trackType = aliases[rawTrackType] || rawTrackType;
  if (VALID_TRACK_TYPES.has(trackType)) return trackType;
  if (nodeType === 'ending') return endingType === 'bad' ? 'lock_be' : 'ending_check';
  return 'main';
};

const normalizeOperator = (value, scope) => {
  const rawOperator = asText(value).trim();
  const aliases = {
    '=': 'eq',
    '==': 'eq',
    '===': 'eq',
    '!=': 'neq',
    '!==': 'neq',
    '>': 'gt',
    '>=': 'gte',
    '<': 'lt',
    '<=': 'lte',
    equals: 'eq',
    notEquals: 'neq',
  };
  const operator = aliases[rawOperator] || rawOperator;
  if (VALID_OPERATORS.has(operator)) return operator;
  return scope === 'flag' ? 'truthy' : 'eq';
};

const readStateKey = (item) => asText(
  item.key
  ?? item.characterId
  ?? item.variableId
  ?? item.flagId
  ?? item.stageId,
).trim();

const cloneBucket = (value) => ({ ...asObject(value) });

export function generateStoryId(prefix = 'story') {
  const safePrefix = asText(prefix, 'story').trim() || 'story';
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return `${safePrefix}-${uuid}`;

  fallbackIdSequence = (fallbackIdSequence + 1) % Number.MAX_SAFE_INTEGER;
  const randomPart = Math.random().toString(36).slice(2, 9);
  return `${safePrefix}-${Date.now().toString(36)}-${fallbackIdSequence.toString(36)}-${randomPart}`;
}

export function normalizeStoryCondition(condition = {}) {
  const source = asObject(condition);
  const scope = normalizeScope(source.scope ?? source.type);
  const normalized = {
    ...source,
    id: asText(source.id).trim() || generateStoryId('condition'),
    scope,
    key: readStateKey(source),
    operator: normalizeOperator(source.operator, scope),
    value: source.value ?? (scope === 'flag' ? true : ''),
  };

  delete normalized.type;
  return normalized;
}

export function normalizeStoryEffect(effect = {}) {
  const source = asObject(effect);
  const scope = normalizeScope(source.scope ?? source.type);
  let operation = asText(source.operation ?? source.operator).trim();

  if (!operation) operation = scope === 'relation' ? 'add' : 'set';
  if (operation === 'increment') operation = 'add';
  if (operation === 'decrement') operation = 'subtract';
  if (operation === 'remove' || operation === 'unset') operation = 'clear';

  const normalized = {
    ...source,
    id: asText(source.id).trim() || generateStoryId('effect'),
    scope,
    key: readStateKey(source),
    operation,
    value: source.value ?? (scope === 'relation' ? 1 : scope === 'flag' ? true : ''),
  };

  delete normalized.type;
  delete normalized.operator;
  return normalized;
}

export const normalizeStoryConditions = (conditions) => (
  asArray(conditions).map(normalizeStoryCondition)
);

export const normalizeStoryEffects = (effects) => (
  asArray(effects).map(normalizeStoryEffect)
);

export function createStoryAnswer(overrides = {}) {
  const source = asObject(overrides);
  const normalized = {
    ...source,
    id: asText(source.id).trim() || generateStoryId('answer'),
    text: asText(source.text ?? source.label, '新的回答'),
    immediateFeedback: asText(source.immediateFeedback ?? source.feedback),
    impactType: normalizeImpactType(source.impactType),
    targetNodeId: asText(source.targetNodeId ?? source.targetId).trim() || null,
    conditions: normalizeStoryConditions(source.conditions),
    effects: normalizeStoryEffects(source.effects),
  };

  delete normalized.label;
  delete normalized.feedback;
  delete normalized.targetId;
  return normalized;
}

export function createStoryNode(overrides = {}) {
  const source = asObject(overrides);
  const nodeType = VALID_NODE_TYPES.has(source.nodeType) ? source.nodeType : 'story';
  const rawPosition = asObject(source.position);
  const content = asText(source.content ?? source.body);
  const scene = asText(source.scene ?? source.location);

  const normalized = {
    ...source,
    id: asText(source.id).trim() || generateStoryId('node'),
    nodeType,
    trackType: normalizeStoryTrackType(
      source.trackType ?? source.routeType ?? source.plotRole ?? source.lineType,
      nodeType,
      source.endingType,
    ),
    title: asText(source.title, nodeType === 'ending' ? '新的结局' : '新的剧情'),
    content,
    question: nodeType === 'ending' ? '' : asText(source.question),
    answers: nodeType === 'ending'
      ? []
      : asArray(source.answers).map(createStoryAnswer),
    position: {
      x: Number.isFinite(Number(rawPosition.x)) ? Number(rawPosition.x) : 120,
      y: Number.isFinite(Number(rawPosition.y)) ? Number(rawPosition.y) : 100,
    },
    chapterId: asText(source.chapterId).trim() || null,
    scene,
    participants: asArray(source.participants).map((id) => asText(id).trim()).filter(Boolean),
    endingType: nodeType === 'ending' ? asText(source.endingType, 'normal') : null,
  };

  // 旧稿中的别名只用于迁移读取，写回时统一使用 canonical 字段。
  delete normalized.body;
  delete normalized.location;
  delete normalized.endingTitle;
  delete normalized.routeType;
  delete normalized.plotRole;
  delete normalized.lineType;
  return normalized;
}

export function createStoryCharacter(overrides = {}, index = 0) {
  const source = asObject(overrides);
  return {
    ...source,
    id: asText(source.id).trim() || generateStoryId('character'),
    name: asText(source.name, '新人物'),
    tagline: asText(source.tagline ?? source.summary),
    personality: asText(source.personality),
    goal: asText(source.goal),
    speechStyle: asText(source.speechStyle),
    secret: asText(source.secret),
    color: asText(source.color, CHARACTER_COLORS[index % CHARACTER_COLORS.length]),
  };
}

export function createDefaultStoryState(projectId = null, overrides = {}) {
  const firstNode = createStoryNode({
    title: '故事开始',
    content: '',
    question: '',
    position: { x: 120, y: 120 },
  });

  return {
    schemaVersion: STORY_SCHEMA_VERSION,
    projectId,
    stateKey: STORY_STATE_KEY,
    title: '互动游戏',
    description: '',
    startNodeId: firstNode.id,
    entryNodeId: firstNode.id,
    nodes: [firstNode],
    characters: [],
    chapters: [],
    variables: [],
    initialState: {
      relations: {},
      flags: {},
      stages: {},
    },
    viewport: { x: 0, y: 0, zoom: 0.85 },
    meta: {},
    ...asObject(overrides),
  };
}

const normalizeInitialState = (value) => {
  const source = asObject(value);
  return {
    ...source,
    relations: cloneBucket(source.relations),
    flags: cloneBucket(source.flags),
    stages: cloneBucket(source.stages),
  };
};

export function normalizeStoryState(value, projectId = null) {
  const source = asObject(value);
  const fallback = createDefaultStoryState(projectId ?? source.projectId ?? null);
  const rawNodes = Array.isArray(source.nodes) && source.nodes.length > 0
    ? source.nodes
    : fallback.nodes;
  const nodes = rawNodes.map((node, index) => createStoryNode({
    ...asObject(node),
    position: asObject(node?.position).x == null && asObject(node?.position).y == null
      ? { x: 120 + (index % 4) * 380, y: 120 + Math.floor(index / 4) * 280 }
      : node.position,
  }));
  const nodeIds = new Set(nodes.map((node) => node.id));
  const connectedNodes = nodes.map((node) => ({
    ...node,
    answers: node.answers.map((answer) => ({
      ...answer,
      targetNodeId: answer.targetNodeId && nodeIds.has(answer.targetNodeId)
        ? answer.targetNodeId
        : null,
    })),
  }));
  const requestedStartNodeId = asText(source.entryNodeId).trim()
    || asText(source.startNodeId).trim();
  const startNodeId = nodeIds.has(requestedStartNodeId) ? requestedStartNodeId : nodes[0].id;
  const viewport = asObject(source.viewport);
  const variables = asArray(source.variables).map((variable, index) => {
    const item = asObject(variable);
    const id = asText(item.id).trim() || generateStoryId('flag');
    return {
      ...item,
      id,
      key: asText(item.key).trim() || `flag_${id.replace(/[^a-zA-Z0-9]/g, '').slice(-10)}`,
      name: asText(item.name, `剧情标记 ${index + 1}`),
      type: 'flag',
      defaultValue: Boolean(item.defaultValue),
    };
  });
  const initialState = normalizeInitialState(source.initialState ?? source.initialVariables);
  variables.forEach((variable) => {
    if (!Object.prototype.hasOwnProperty.call(initialState.flags, variable.key)) {
      initialState.flags[variable.key] = variable.defaultValue;
    }
  });

  return {
    ...fallback,
    ...source,
    schemaVersion: STORY_SCHEMA_VERSION,
    projectId: projectId ?? source.projectId ?? null,
    stateKey: STORY_STATE_KEY,
    title: asText(source.title, fallback.title),
    description: asText(source.description),
    startNodeId,
    entryNodeId: startNodeId,
    nodes: connectedNodes,
    characters: asArray(source.characters).map(createStoryCharacter),
    chapters: asArray(source.chapters).map((chapter, index) => {
      const item = asObject(chapter);
      return {
        ...item,
        id: asText(item.id).trim() || generateStoryId('chapter'),
        title: asText(item.title, `第 ${index + 1} 章`),
        order: Number.isFinite(Number(item.order)) ? Number(item.order) : index,
      };
    }),
    variables,
    initialState,
    viewport: {
      x: Number.isFinite(Number(viewport.x)) ? Number(viewport.x) : fallback.viewport.x,
      y: Number.isFinite(Number(viewport.y)) ? Number(viewport.y) : fallback.viewport.y,
      zoom: Number.isFinite(Number(viewport.zoom)) ? Number(viewport.zoom) : fallback.viewport.zoom,
    },
    meta: { ...asObject(source.meta) },
  };
}

export function deriveStoryEdges(nodes = []) {
  const safeNodes = asArray(nodes);
  const nodeIds = new Set(safeNodes.map((node) => asText(node?.id).trim()).filter(Boolean));
  const nodeById = new Map(safeNodes.map((node) => [asText(node?.id).trim(), node]));
  const edges = [];

  safeNodes.forEach((node) => {
    const sourceId = asText(node?.id).trim();
    if (!sourceId) return;

    asArray(node?.answers).forEach((rawAnswer) => {
      const answer = createStoryAnswer(rawAnswer);
      if (!answer.targetNodeId || !nodeIds.has(answer.targetNodeId)) return;
      const impact = IMPACT_TYPES[answer.impactType] || IMPACT_TYPES.local;
      const targetNode = nodeById.get(answer.targetNodeId);
      const trackType = normalizeStoryTrackType(
        targetNode?.trackType,
        targetNode?.nodeType,
        targetNode?.endingType,
      );
      const track = STORY_TRACK_TYPES[trackType] || STORY_TRACK_TYPES.main;

      edges.push({
        id: `edge-${sourceId}-${answer.id}-${answer.targetNodeId}`,
        source: sourceId,
        sourceHandle: createStoryAnswerHandleId(answer.id),
        target: answer.targetNodeId,
        targetHandle: 'target',
        type: 'smoothstep',
        label: answer.text || '未命名回答',
        animated: false,
        className: `interactive-story-edge interactive-story-edge--track-${trackType} interactive-story-edge--impact-${answer.impactType}`,
        style: {
          stroke: track.color,
          strokeWidth: impact.edgeWidth,
          strokeDasharray: trackType === 'lock_be' ? '6 5' : impact.edgeDasharray,
        },
        labelStyle: {
          fill: track.color,
          fontSize: 11,
          fontWeight: 600,
        },
        labelBgStyle: {
          fill: 'var(--color-bg-secondary)',
          fillOpacity: 0.86,
          stroke: 'var(--color-border-secondary)',
          strokeWidth: 0.5,
        },
        labelBgPadding: [5, 3],
        labelBgBorderRadius: 4,
        data: {
          answerId: answer.id,
          answerIds: [answer.id],
          impactType: answer.impactType,
          trackType,
          answers: [answer],
        },
      });
    });
  });

  return edges;
}

export function createInitialRuntimeState(storyState, overrides = {}) {
  const story = normalizeStoryState(storyState);
  const initial = normalizeInitialState(story.initialState);
  const extra = normalizeInitialState(overrides);

  return {
    relations: { ...initial.relations, ...extra.relations },
    flags: { ...initial.flags, ...extra.flags },
    stages: { ...initial.stages, ...extra.stages },
  };
}

export const normalizeRuntimeState = normalizeInitialState;

const readConditionValue = (runtimeState, condition) => {
  const runtime = asObject(runtimeState);
  const bucketName = condition.scope === 'relation'
    ? 'relations'
    : condition.scope === 'stage'
      ? 'stages'
      : 'flags';
  return asObject(runtime[bucketName])[condition.key];
};

const compareValues = (actual, operator, expected) => {
  switch (operator) {
    case 'neq':
      return actual !== expected;
    case 'gt':
      return Number(actual) > Number(expected);
    case 'gte':
      return Number(actual) >= Number(expected);
    case 'lt':
      return Number(actual) < Number(expected);
    case 'lte':
      return Number(actual) <= Number(expected);
    case 'truthy':
      return Boolean(actual);
    case 'falsy':
      return !actual;
    case 'in':
      return asArray(expected).includes(actual);
    case 'notIn':
      return !asArray(expected).includes(actual);
    case 'eq':
    default:
      return actual === expected;
  }
};

export function evaluateCondition(condition, runtimeState) {
  const normalized = normalizeStoryCondition(condition);
  if (normalized.enabled === false) return true;
  if (!normalized.key) return true;

  const actual = readConditionValue(runtimeState, normalized);
  const expected = normalized.scope === 'relation'
    ? Number(normalized.value)
    : normalized.value;
  const comparableActual = normalized.scope === 'relation' ? Number(actual ?? 0) : actual;
  return compareValues(comparableActual, normalized.operator, expected);
}

export function evaluateConditions(conditions, runtimeState, mode = 'all') {
  const list = normalizeStoryConditions(conditions).filter((item) => item.enabled !== false);
  if (list.length === 0) return true;
  if (mode === 'any') return list.some((item) => evaluateCondition(item, runtimeState));
  return list.every((item) => evaluateCondition(item, runtimeState));
}

export function applyEffects(runtimeState, effects) {
  const next = {
    relations: cloneBucket(runtimeState?.relations),
    flags: cloneBucket(runtimeState?.flags),
    stages: cloneBucket(runtimeState?.stages),
  };

  normalizeStoryEffects(effects).forEach((effect) => {
    if (!effect.key || effect.enabled === false) return;

    if (effect.scope === 'relation') {
      const current = Number(next.relations[effect.key] ?? 0);
      const amount = Number(effect.value ?? 0);
      const safeCurrent = Number.isFinite(current) ? current : 0;
      const safeAmount = Number.isFinite(amount) ? amount : 0;
      if (effect.operation === 'set') next.relations[effect.key] = safeAmount;
      else if (effect.operation === 'subtract') next.relations[effect.key] = safeCurrent - safeAmount;
      else if (effect.operation === 'clear') delete next.relations[effect.key];
      else next.relations[effect.key] = safeCurrent + safeAmount;
      return;
    }

    const bucket = effect.scope === 'stage' ? next.stages : next.flags;
    if (effect.operation === 'clear') delete bucket[effect.key];
    else if (effect.operation === 'toggle') bucket[effect.key] = !bucket[effect.key];
    else bucket[effect.key] = effect.value;
  });

  return next;
}

export function getAvailableAnswers(node, runtimeState) {
  return asArray(node?.answers)
    .map(createStoryAnswer)
    .filter((answer) => evaluateConditions(answer.conditions, runtimeState, answer.conditionMode));
}

const previewStatusForNode = (node) => {
  if (!node) return 'invalid';
  return node.nodeType === 'ending' ? 'ended' : 'playing';
};

export function startPreview(storyState, options = {}) {
  const story = normalizeStoryState(storyState);
  const requestedNodeId = asText(options.entryNodeId).trim()
    || asText(options.startNodeId).trim();
  const startNodeId = story.nodes.some((node) => node.id === requestedNodeId)
    ? requestedNodeId
    : story.entryNodeId || story.startNodeId;
  const currentNode = story.nodes.find((node) => node.id === startNodeId) || null;
  const runtime = createInitialRuntimeState(story, options.runtime ?? options.state);

  return {
    currentNodeId: currentNode?.id || null,
    runtime,
    status: previewStatusForNode(currentNode),
    history: [],
    visitedNodeIds: currentNode ? [currentNode.id] : [],
    lastFeedback: '',
    ending: currentNode?.nodeType === 'ending' ? currentNode : null,
    error: currentNode ? null : '找不到剧情起点',
  };
}

export function advancePreview(storyState, session, answerId, options = {}) {
  const story = normalizeStoryState(storyState);
  const currentSession = asObject(session);
  const currentNode = story.nodes.find((node) => node.id === currentSession.currentNodeId);
  const runtime = currentSession.runtime
    ? normalizeInitialState(currentSession.runtime)
    : createInitialRuntimeState(story);

  if (!currentNode) {
    return { ...currentSession, runtime, status: 'invalid', error: '当前剧情节点不存在' };
  }
  if (currentNode.nodeType === 'ending') {
    return { ...currentSession, runtime, status: 'ended', ending: currentNode, error: null };
  }

  const answer = currentNode.answers.find((item) => item.id === answerId);
  if (!answer) {
    return { ...currentSession, runtime, error: '所选回答不存在' };
  }
  if (!evaluateConditions(answer.conditions, runtime, answer.conditionMode)) {
    return { ...currentSession, runtime, error: '当前状态不满足这个回答的出现条件' };
  }

  const nextRuntime = applyEffects(runtime, answer.effects);
  const nextNode = story.nodes.find((node) => node.id === answer.targetNodeId) || null;
  const history = [
    ...asArray(currentSession.history),
    {
      nodeId: currentNode.id,
      answerId: answer.id,
      answerText: answer.text,
      targetNodeId: answer.targetNodeId,
      impactType: answer.impactType,
      effects: answer.effects,
    },
  ];
  const maxSteps = Number.isFinite(Number(options.maxSteps)) ? Number(options.maxSteps) : 500;
  const reachedEnding = nextNode?.nodeType === 'ending';
  const exceededStepLimit = history.length >= maxSteps && !reachedEnding;
  const status = reachedEnding
    ? 'ended'
    : exceededStepLimit
      ? 'loop'
      : nextNode
        ? previewStatusForNode(nextNode)
        : 'unfinished';

  return {
    ...currentSession,
    currentNodeId: nextNode?.id || null,
    runtime: nextRuntime,
    status,
    history,
    visitedNodeIds: [
      ...asArray(currentSession.visitedNodeIds),
      ...(nextNode ? [nextNode.id] : []),
    ],
    lastFeedback: answer.immediateFeedback,
    ending: nextNode?.nodeType === 'ending' ? nextNode : null,
    error: exceededStepLimit
      ? `预演超过 ${maxSteps} 步，可能存在无法退出的循环`
      : nextNode
        ? null
        : '这个回答还没有连接后续剧情',
  };
}

export const createPreviewSession = startPreview;
export const choosePreviewAnswer = advancePreview;
